/**
 * THE CLAIM, FROM THE SHOP DESK.
 *
 * Anthony, 2026-10-07: "if a shop desk member works on it, tag them as the
 * advisor, do the same inside mechanicrank where eber works so they don't
 * step on each others toes."
 *
 * The first person here to act on a pre-purchase inspection -- send the
 * quote, raise or record the prepayment, book it -- becomes its advisor,
 * and a 'claimed' row says who and that it was the Shop Desk. The
 * MechanicRank desk reads the same rows through the bridge
 * (server/routes/ppi.ts) and is refused a job somebody here already has.
 *
 * It WARNS, it does not block. Somebody covering for a colleague who is
 * off sick has to be able to take the payment; what they must not be is
 * surprised that it was somebody else's.
 *
 * And it never throws. A claim is bookkeeping about the work; the work
 * itself -- the money, the booking -- must not fail because of it.
 */

import { db, api } from "./client.ts";
import { whoAmI } from "./session.ts";
import { isPpiJob, viaLabel, PPI_ACCOUNT } from "../domain/ppi.ts";

export type PpiFacts = {
  ppi: boolean; status: string; accountId: string; advisorId: string | null;
  title: string | null; complaint: string | null;
};

/** What this job is, for the money rules. One read for the job, one for the lines. */
export async function ppiFacts(jobId: string): Promise<PpiFacts | null> {
  const sb = await db();
  const { data: j } = await sb.from("jobs")
    .select("status,account_id,advisor_id,title,complaint").eq("id", jobId).maybeSingle();
  const job = j as { status: string; account_id: string; advisor_id: string | null;
    title: string | null; complaint: string | null } | null;
  if (!job) return null;
  const { data: lines } = await sb.from("line_items").select("description").eq("job_id", jobId);
  return {
    ppi: isPpiJob({ title: job.title, complaint: job.complaint,
      lines: (lines ?? []) as Array<{ description: string | null }> }),
    status: job.status, accountId: job.account_id, advisorId: job.advisor_id,
    title: job.title, complaint: job.complaint,
  };
}

export type ClaimSeen = { name: string | null; profileId: string; via: string; mine: boolean };

/** Who has this job, and whether it is the person reading it. */
export async function claimOf(jobId: string, advisorId: string | null): Promise<ClaimSeen | null> {
  if (!advisorId) return null;
  try {
    const sb = await db();
    const { data: me } = await sb.auth.getUser();
    const [{ data: p }, { data: ev }] = await Promise.all([
      sb.from("profiles").select("full_name").eq("id", advisorId).maybeSingle(),
      sb.from("job_events").select("meta,actor_id").eq("job_id", jobId)
        .in("kind", ["claimed", "reassigned"]).order("created_at", { ascending: false }).limit(1),
    ]);
    const last = ((ev ?? []) as Array<{ meta: Record<string, unknown> | null; actor_id: string | null }>)[0];
    return {
      name: (p as { full_name: string | null } | null)?.full_name ?? null,
      profileId: advisorId,
      via: last && last.actor_id === advisorId ? String(last.meta?.["via"] ?? "shop-desk") : "shop-desk",
      mine: me.user?.id === advisorId,
    };
  } catch {
    return null;
  }
}

/**
 * TAKE IT, IF NOBODY HAS. Returns who has it when that is somebody else,
 * so the screen can say so. Only on a PPI; any other job is left exactly
 * as it was, advisor and all.
 */
export async function claimPpi(jobId: string, what: string): Promise<ClaimSeen | null> {
  try {
    const f = await ppiFacts(jobId);
    if (!f || !f.ppi) return null;
    const sb = await db();
    const { data: me } = await sb.auth.getUser();
    const uid = me.user?.id;
    if (!uid) return null;
    if (f.advisorId && f.advisorId !== uid) {
      const other = await claimOf(jobId, f.advisorId);
      return other ? { ...other, mine: false } : null;
    }
    if (f.advisorId === uid) return null;
    /* Only where it is still empty, read back: two people pressing in the
       same second must not both believe they have it. */
    await sb.from("jobs").update({ advisor_id: uid }).eq("id", jobId).is("advisor_id", null);
    const { data: now } = await sb.from("jobs").select("advisor_id").eq("id", jobId).maybeSingle();
    const got = (now as { advisor_id: string | null } | null)?.advisor_id ?? null;
    if (got !== uid) return got ? await claimOf(jobId, got) : null;
    const { data: prof } = await sb.from("profiles").select("full_name").eq("id", uid).maybeSingle();
    const name = (prof as { full_name: string | null } | null)?.full_name ?? "Staff";
    await sb.from("job_events").insert({
      account_id: f.accountId, job_id: jobId, kind: "claimed", actor: "staff", actor_id: uid,
      title: `${name} took this inspection (via ${viaLabel("shop-desk")})`,
      detail: `First to act on it: ${what}.`, visible_to_customer: false,
      meta: { via: "shop-desk", what },
    });
    return null;
  } catch {
    return null;
  }
}

/** The warning, in the house voice. Empty when there is nothing to say. */
export function claimWarning(c: ClaimSeen | null): string {
  if (!c || c.mine) return "";
  return `${c.name ?? "Somebody else"} is the advisor on this one (via ${viaLabel(c.via)}). `
    + `It went through, and it is still theirs. Let them know.`;
}

/* ───────────────────── send to the PPI desk ───────────────────── */

/**
 * SEND TO PPI DESK (server/routes/ppi-handoff.ts). Only Supercanic has PPIs
 * to send, so the button is only drawn there; another shop with a lead that
 * happens to say "pre-purchase" never sees it.
 */
export async function maySendToPpiDesk(): Promise<boolean> {
  const w = await whoAmI().catch(() => null);
  return !!w && w.state === "in" && w.account.id === PPI_ACCOUNT && w.profile.role !== "viewer";
}

export async function sendToPpiDesk(ref: { enquiry_id?: string; job_id?: string }):
  Promise<{ ok: true; open: string; code: number | null } | { ok: false; error: string }> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  const res = await fetch(api("/api/ppi-handoff"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
    body: JSON.stringify(ref),
  });
  const j = await res.json().catch(() => ({})) as { open?: string; code?: number; error?: string };
  return res.ok && j.open ? { ok: true, open: j.open, code: j.code ?? null }
    : { ok: false, error: j.error ?? "The PPI desk did not take it." };
}

/** The button's click: confirm, send, then open the request on the PPI desk. */
export function wireSendToPpiDesk(btn: HTMLButtonElement | null, ref: { enquiry_id?: string; job_id?: string },
  say: (t: string, bad?: boolean) => void): void {
  btn?.addEventListener("click", async () => {
    if (btn.dataset["sure"] !== "1") {
      btn.dataset["sure"] = "1";
      btn.textContent = "Tap again: this closes it here";
      return;
    }
    btn.disabled = true;
    const r = await sendToPpiDesk(ref);
    if (!r.ok) { btn.disabled = false; say(r.error, true); return; }
    say(`Sent as P-${r.code ?? "?"}. Supercanic will not text them about it again.`);
    window.open(r.open, "_blank", "noopener");
  });
}
